import { JourneyError } from "../contracts";
import type { DeliveryEstimate, PurchaseOpened, PurchaseSignalled } from "../contracts";
import {
  attemptReadOf,
  StartRefused,
  startRefusalOf,
  watchAttempt,
  type AttemptChanges,
  type PurchaseTransport,
} from "./transport";

/**
 * The purchase sheet over the Platform API (`/platform/buy-on-behalf`). The
 * host gives the signed-in read of the purchase (`read`: Link in Bio's
 * viewer read of the access), the buyer's identity (its identity frame) and,
 * where it has one, what says an attempt changed (`changes`: the API's
 * realtime socket); a watched attempt is read at once on each change, and
 * polled as the fallback.
 * A caller error is the server's answer: its code, and a refused start's
 * words (`StartRefused`); anything else may not be.
 */
export function purchaseWebTransport(
  baseUrl: string,
  headers: () => Promise<Record<string, string>>,
  host: Pick<PurchaseTransport, "read" | "identity"> & { changes?: AttemptChanges },
): PurchaseTransport {
  const { changes, ...reads } = host;
  const call = async <T = unknown>(path: string, body?: unknown, keepalive = false): Promise<T> => {
    const url = `${baseUrl}/buy-on-behalf/${path}`;
    const json = { "Content-Type": "application/json" };
    // One that outlives the page goes at once, without the bearer: there
    // may be no time to read it, and the capability is enough.
    const response = keepalive
      ? await fetch(url, { method: "POST", headers: json, body: JSON.stringify(body), keepalive: true })
      : await fetch(url, {
          method: body === undefined ? "GET" : "POST",
          headers: { ...(await headers()), ...json },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
    if (!response.ok) {
      const answer: unknown =
        response.status >= 400 && response.status < 500 ? await response.json().catch(() => undefined) : undefined;
      const code =
        answer && typeof answer === "object" && "error" in answer && typeof answer.error === "string"
          ? answer.error
          : null;
      const refusal = code && answer && typeof answer === "object" && "refusal" in answer ? startRefusalOf(answer.refusal) : null;
      throw Object.assign(
        code === null ? Error("request_failed") : refusal ? new StartRefused(code, refusal) : new JourneyError(code),
        { status: response.status },
      );
    }
    return (await response.json()) as T;
  };
  return {
    ...reads,
    beginCardCapture: (cardId) => call("cards/capture", { cardId }),
    completeCardCapture: (input) => call("cards/capture/complete", input),
    estimateDelivery: (input) => call<DeliveryEstimate>("delivery-estimates", input),
    deliveryEstimate: (sheetId, id) =>
      call<DeliveryEstimate>(`delivery-estimates/${encodeURIComponent(id)}?sheetId=${encodeURIComponent(sheetId)}`),
    prepare: async (input) => {
      await call("vendor-account", input);
    },
    open: (input) => call<PurchaseOpened>("purchases", input),
    signal: ({ purchaseId, attachmentId, secret }, signal, options) =>
      call<PurchaseSignalled>(
        `purchases/${encodeURIComponent(purchaseId)}/signals`,
        { attachmentId, secret, signal },
        options?.keepalive,
      ),
    start: async (input) => attemptReadOf(await call("attempts", input)),
    active: async (accessId) => {
      const found = await call(`active?accessId=${encodeURIComponent(accessId)}`);
      return found === null ? null : attemptReadOf(found);
    },
    status: async (id) => attemptReadOf(await call(`attempts/${encodeURIComponent(id)}`)),
    cancel: async (id) => attemptReadOf(await call(`attempts/${encodeURIComponent(id)}/cancel`, {})),
    answer: async (id, answer) => attemptReadOf(await call(`attempts/${encodeURIComponent(id)}/answer`, answer)),
    watch(id, onStatus, onError) {
      return watchAttempt({
        id,
        poll: async () => attemptReadOf(await call(`attempts/${encodeURIComponent(id)}`)),
        onStatus,
        onError,
        changes,
      });
    },
  };
}
